import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AppProvider, Page, Card, Thumbnail, DisplayText, Button, Badge, ButtonGroup, Modal, Select } from '@shopify/polaris';
import translations from '@shopify/polaris/locales/es.json';
import axios from 'utils/axios';
import { ta } from 'utils';
import { ViewMinor, ExportMinor } from '@shopify/polaris-icons';
import InformativeModal from '@presentational/order_supply/InformativeModal';

import PolarisRecordsTable from '@components/shared/ui/PolarisRecordsTable';

const propTypes = {
  orderSupply: PropTypes.object.isRequired,
  submitSyncURL: PropTypes.string,
  isAdmin: PropTypes.bool,
  sellerSpecialCostURL: PropTypes.string,
  
  exportXlsxUrl: PropTypes.string.isRequired,

  // If present, allow to place purchase order. Additional info is required based on the vendor
  placePurchaseOrderInfo: PropTypes.shape({
    url: PropTypes.string.isRequired,
    requireShipping: PropTypes.bool.isRequired,
    deliveryTypeOptions: PropTypes.arrayOf(PropTypes.string),
  }),
}
const defaultProps = {}

const OrderSupplyShow = (props) => {
  const [loading, setLoading] = useState(false);
  const [providerPoId, setProviderPoId] = useState(props.orderSupply.provider_po_id); // Display providerPoId if it exists. Also disable some actions if already exists

  const { orderSupply } = props;
  // TODO use one state for records instead of two, because only one are displayed at a time
  const [resultModal, setResultModal] = useState({
    title: "",
    open: false,
    dataTable: {
      columnsData: [],
      items: [],
    },
    errors: [],
  })

  // State for delivery type selection modal
  const [deliveryTypeModal, setDeliveryTypeModal] = useState({
    open: false,
    deliveryType: '',
  })

  // Return object in case totals or another calculable values is required.
  // withProviderPoData: If true, add columns for provider PO data (Intcomex/Dataformas)
  const buildTableData = (withProviderPoData) => {
    let columnsData = [
      { header: "", accessor: ({ item_image_url }) => <Thumbnail source={item_image_url} /> },
      { header: ta("supply_item.title"), accessor: "display_title" },
      { header: ta("general.sku"), accessor: "sku" },
      { header: ta("supply_item.supplier_sku"), accessor: "supplier_sku" },
      // { header: ta("supply_item.quantity_shipped"), accessor: "quantity_shipped" }, // Removed from UI as it is no longer required
    ];
    
    if (withProviderPoData) {
      columnsData = [
        ...columnsData,
        { header: ta("supply_item.po_row_created"), accessor: ({ po_row_created }) => (
          <Badge status={po_row_created ? "info" : "warning"}>
            { I18n.t(String(po_row_created)) }
          </Badge> 
        )},
        { header: ta("supply_item.po_quantity_purchased"), accessor: "po_quantity_purchased" },
      ]
    }

    // These goes at the end
    columnsData = [
      ...columnsData,
      { header: ta("general.quantity"), accessor: "quantity" },
      { header: ta("supply_item.unit_price"), accessor: "unit_price_money" },
      { header: ta("supply_item.total"), accessor: "total_money" },
    ];

    return {
      columnsData,
    }
  }

  const tableFooterMarkup = (
    <div className='flex justify-end'>
      <div className='flex flex-column' style={{ minWidth: '150px' }}>
        <div className='flex justify-between items-center'>
          <p className='mr20'>{ ta('order_supply.subtotal')}</p>
          <p>{ orderSupply.subtotal_money }</p>
        </div>
        
        <div className='flex justify-between items-center'>
          <p className='mr20'>{ ta('order_supply.tax_total')}</p>
          <p>{ orderSupply.tax_total_money }</p>
        </div>
        <hr style={{ borderStyle: 'solid', width: '100%' }} />
        <div className='flex justify-between items-center'>
          <p className='mr20'>{ ta('order_supply.total')}</p>
          <p>{ orderSupply.total_money }</p>
        </div>
      </div>
    </div>
  )

  const handleCloseModal = useCallback(() => { setResultModal({ ...resultModal, open: false }) }, [resultModal]);

  const submitSyncWithBrightPearl = async () => {
    setLoading(true);
    let response = await axios.post(props.submitSyncURL);
    if (response.data.success) {
      alert("Tarea asignada, recargue unos momentos para ver los cambios.")
    }
    else {
      alert(I18n.t("messages.request_error"))
    }
    setLoading(false);
  }

  /**
   * Submit to create a PO via API
   * Results are shown in the InformativeModal
   */
  const submitCreatePO = async (deliveryType = null) => {
    setLoading(true);
    const modalTitle = "Crear PO via API";
    try {
      const requestData = deliveryType ? { delivery_type: deliveryType } : {};
      const { data } = await axios.post(props.placePurchaseOrderInfo.url, requestData);
      console.log("submitCreatePO", data);
      if (data.success) {
        setResultModal({
          title: modalTitle,
          open: true,
          dataTable: {
            columnsData: [
              { header: ta('general.sku'), accessor: "sku" },
              { header: ta('supply_item.po_row_created'), accessor: ({ po_row_created }) => (
                <Badge status={ po_row_created ? "info" : "warning" }>
                  { po_row_created ? I18n.t("true") : I18n.t("false") }
                </Badge>
              )},
              { header: ta('supply_item.po_quantity_purchased'), accessor: "po_quantity_purchased" },
            ],
            items: data.records,
          },
          errors: data.errors,
        });

        setProviderPoId(data.provider_po_id)
      } else {
        setResultModal({
          title: modalTitle,
          open: true,
          dataTable: {
            columnsData: [],
            items: [],
          },
          errors: data.errors, // should be errors or messages
        });
      }
    } catch (e) {
      alert("error intentelo mas tarde")
      console.log("EEE", e);
    }
    setLoading(false);
  }

  /**
   * Handle click on "Crear PO via API" button
   * If requireShipping is true, open modal to select delivery type
   * Otherwise, submit directly
   */
  const handleCreatePOClick = useCallback(() => {
    if (props.placePurchaseOrderInfo?.requireShipping) {
      setDeliveryTypeModal({ open: true, deliveryType: '' });
    } else {
      submitCreatePO();
    }
  }, [props.placePurchaseOrderInfo]);

  /**
   * Handle delivery type selection change
   */
  const handleDeliveryTypeChange = useCallback((value) => {
    setDeliveryTypeModal(prev => ({ ...prev, deliveryType: value }));
  }, []);

  /**
   * Handle confirm delivery type and submit
   */
  const handleConfirmDeliveryType = useCallback(() => {
    if (!deliveryTypeModal.deliveryType) {
      alert("Por favor seleccione un tipo de entrega");
      return;
    }
    const selectedDeliveryType = deliveryTypeModal.deliveryType;
    setDeliveryTypeModal({ open: false, deliveryType: '' });
    submitCreatePO(selectedDeliveryType);
  }, [deliveryTypeModal.deliveryType]);

  /**
   * Handle close delivery type modal
   */
  const handleCloseDeliveryTypeModal = useCallback(() => {
    setDeliveryTypeModal({ open: false, deliveryType: '' });
  }, []);


  /**
   * Request special costs from seller (read only)
   * Results are shown in the InformativeModal
   */
  const requestSpecialCost = async () => {
    setLoading(true);
    const modalTitle = "Consulta de costos especiales";
    try {
      // Request local endpoint to get data formatted for the modal (internally query from Hub API)
      const { data } = await axios.get(props.sellerSpecialCostURL);
      console.log("requestSpecialCost", data);

      if (data.success) {
        setResultModal({
          title: modalTitle,
          open: true,
          dataTable: {
            columnsData: [
              { header: ta('general.sku'), accessor: "sku" },
              { header: ta("supply_item.supplier_sku"), accessor: "supplier_sku" },
              { header: ta('general.unit_cost'), accessor: "unit_cost" },
            ],
            items: data.records,
          },
          errors: [],
        });
      } else {
        setResultModal({
          title: modalTitle,
          open: true,
          dataTable: {
            columnsData: [],
            items: [],
          },
          errors: data.errors, // should be errors or messages
        });
      }
    } catch (e) {
      alert("error intentelo mas tarde")
      console.log("EEE", e);
    }
    setLoading(false)
  }
  const brightPearlPoURL = props.isAdmin && `https://euw1.brightpearlapp.com/patt-op.php?scode=invoice&oID=${orderSupply.bright_pearl_po_id}` // Only production URL is set

  // Build table data to display information. When PO can be placed in provider API add extra columns
  const tableData = buildTableData(!!props.placePurchaseOrderInfo);

  let secondaryActions = [
    {
      content: 'Exportar XLSX',
      url: props.exportXlsxUrl,
      icon: ExportMinor,
    }
  ];

  if(brightPearlPoURL){
    secondaryActions = [
      ...secondaryActions,
      {
        content: 'Ver PO en Brightpearl',
        external: true,
        url: brightPearlPoURL,
        icon: ViewMinor
      }
    ]
  }

  console.log("orderSupply", orderSupply);

  console.log("Props", props)

  return (
    <AppProvider i18n={translations}>
      <Page
        fullWidth
        breadcrumbs={[{ content: I18n.t("actions.back"), url: '/order_supplies' }]}
        title={`${orderSupply.display_bright_pearl_id}`}
        subtitle={!!providerPoId ? `Número de orden en proveedor: ${providerPoId}` : undefined}
        titleMetadata={orderSupply.created_at_text}
        secondaryActions={secondaryActions}
      >
        <div className="mb20">
          <div className='flex'>
            <div className="mb10 flex1">
              <DisplayText size="medium">
                {orderSupply.vendor}
              </DisplayText>
              <Badge status={"success"}>
                {I18n.t(`enum.status.${orderSupply.status}`)}
              </Badge>
            </div>

            {/* Show action to create PO via API if not already created and if allowed */}
            {(!providerPoId && props.placePurchaseOrderInfo) &&
              <div className='mb10 alignSelfCenter'>
                <Button
                  loading={loading}
                  onClick={handleCreatePOClick}
                >
                  Crear PO via API
                </Button>
              </div>
            }
          </div>

          <Card>
            <PolarisRecordsTable 
              items={orderSupply.supply_items}
              columnsData={tableData.columnsData}
              dataTableProps={{
                footerContent: tableFooterMarkup
              }}
            />

            {props.submitSyncURL &&
              <Card.Section>
                <ButtonGroup>
                  <Button
                    loading={loading}
                    onClick={submitSyncWithBrightPearl}
                  >
                    Sincronizar manualmente con Brightpearl
                  </Button>
                  <Button
                    loading={loading}
                    onClick={requestSpecialCost}
                  >
                    Consulta de costos especiales
                  </Button>
                </ButtonGroup>
              </Card.Section>
            }

          </Card>

          {/* Modal with table with multiple usages:
            - Show results for Intcomex buy process
            - Show results from Seller Special Costs
          */}
          <InformativeModal
            open={resultModal.open}
            title={resultModal.title}
            columnsData={resultModal.dataTable.columnsData}
            items={resultModal.dataTable.items}
            errors={resultModal.errors}
            onClose={handleCloseModal}
          />

          {/* Modal for delivery type selection when requireShipping is true */}
          <Modal
            open={deliveryTypeModal.open}
            onClose={handleCloseDeliveryTypeModal}
            title="Seleccionar tipo de entrega"
            primaryAction={{
              content: 'Confirmar',
              onAction: handleConfirmDeliveryType,
              disabled: !deliveryTypeModal.deliveryType || loading,
              loading: loading,
            }}
            secondaryActions={[
              {
                content: 'Cancelar',
                onAction: handleCloseDeliveryTypeModal,
              },
            ]}
          >
            <Modal.Section>
              <Select
                label="Tipo de entrega"
                options={[
                  { label: 'Seleccione un tipo de entrega', value: '', disabled: true },
                  ...(props.placePurchaseOrderInfo?.deliveryTypeOptions || []).map(type => ({
                    label: type,
                    value: type,
                  })),
                ]}
                value={deliveryTypeModal.deliveryType}
                onChange={handleDeliveryTypeChange}
              />
            </Modal.Section>
          </Modal>
        </div>
      </Page>
    </AppProvider>
  )
};

OrderSupplyShow.propTypes = propTypes;
OrderSupplyShow.defaultProps = defaultProps;

export default OrderSupplyShow;
